/**
 * Band display rules for the r5 student surfaces (Yousef's guideline, 8 Oct 2026; DECISIONS 12).
 *
 * Three rules live here so no page can break them:
 *  1. A band is always shown with one decimal (6.0, never 6).
 *  2. A missing, zero or non-finite band is never rendered as a number. `isShowableBand` is the guard,
 *     `formatShowableBand` returns null for anything that fails it, and `BandNumber` / `BandLockup`
 *     (components/r5) use both, so "0.0" cannot reach the screen through them.
 *  3. Band colour comes from one place, `bandRange`.
 *
 * Nothing here derives a band. Every value is read from stored grader output (Law 4).
 */

export type BandRange = 'min' | 'low' | 'mid' | 'high'

/** True for a finite number above zero. `0`, `0.0`, `NaN`, `null`, strings and `undefined` all fail. */
export function isShowableBand(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

/**
 * Colour range of a band (GL section 3): below 5.0, 5.0 to 5.5, 6.0 to 6.5, 7.0 and up.
 * A quarter value such as 6.25 (a Writing task mean) takes the range of its value.
 */
export function bandRange(value: number): BandRange {
  if (value < 5) return 'min'
  if (value < 6) return 'low'
  if (value < 7) return 'mid'
  return 'high'
}

/** One decimal, Western digits. Does not guard: use `formatShowableBand` when the value can be missing. */
export function formatBandValue(value: number): string {
  return value.toFixed(1)
}

/** True when the value sits on the IELTS scale: a whole or half band (6.0, 6.5). */
export function isHalfBand(value: number): boolean {
  return Number.isInteger(value * 2)
}

/**
 * A Writing task band as stored. The task value is the mean of the four criterion bands, so it can be a quarter (5.75), which
 * is not a band on the IELTS scale: it is shown exactly (5.75, 5.625) and the page captions it as an average, never rounded
 * to a half band the student could read as a real band. Whole and half values keep one decimal.
 */
export function formatTaskBand(value: number): string {
  // The mean of four half bands is a multiple of 0.125, so three decimals are exact; trailing zeros are dropped (5.75, 5.625).
  return isHalfBand(value) ? value.toFixed(1) : String(Number(value.toFixed(3)))
}

/** `"6.5"` for a showable band, `null` for anything else (the caller shows "No band yet" or a skeleton). */
export function formatShowableBand(value: unknown): string | null {
  return isShowableBand(value) ? formatBandValue(value) : null
}

/**
 * A target band from a stored value: a number or a string such as "7", "7.0" or "6.5". Returns a
 * half band between 5.0 and 9.0, or null. Mirrors the planner's `parse_target_band`.
 */
export function parseTargetBand(raw: unknown): number | null {
  let value: number
  if (typeof raw === 'number') value = raw
  else if (typeof raw === 'string' && /^\s*\d(?:\.\d+)?\s*$/.test(raw)) value = Number(raw)
  else return null
  if (!Number.isFinite(value) || value < 5 || value > 9) return null
  return Number.isInteger(value * 2) ? value : null
}

/**
 * The lowest skill(s) among the showable bands (GL: the Lowest tag, yellow tint, never red). Empty
 * unless at least two skills have a band and they are not all equal, so a single result or a flat
 * profile does not point at anything.
 */
export function lowestSkills<S extends string>(skills: ReadonlyArray<{ skill: S; band: number | null | undefined }>): Set<S> {
  const banded = skills.filter((entry): entry is { skill: S; band: number } => isShowableBand(entry.band))
  if (banded.length < 2) return new Set()
  const min = Math.min(...banded.map((entry) => entry.band))
  if (banded.every((entry) => entry.band === min)) return new Set()
  return new Set(banded.filter((entry) => entry.band === min).map((entry) => entry.skill))
}

/** The strongest skill(s): the mirror of `lowestSkills`, empty unless at least two skills have a band and they are not all equal. */
export function highestSkills<S extends string>(skills: ReadonlyArray<{ skill: S; band: number | null | undefined }>): Set<S> {
  const banded = skills.filter((entry): entry is { skill: S; band: number } => isShowableBand(entry.band))
  if (banded.length < 2) return new Set()
  const max = Math.max(...banded.map((entry) => entry.band))
  if (banded.every((entry) => entry.band === max)) return new Set()
  return new Set(banded.filter((entry) => entry.band === max).map((entry) => entry.skill))
}
